<template>
  <div>
    <Button
      label="Add File"
      size="small"
      @click="toggleOpen"
    >
      <template #icon>
        <Icon name="mdi-plus" />
      </template>
    </Button>

    <Dialog
      v-model:visible="visible"
      header="Add File"
      :style="{ width: '600px' }"
      :modal="true"
    >
      <div class="">
        <UploaderPublicStorage
          ref="uploaderRef"
          v-model:selectedFiles="stagedFiles"
          v-model:uploadedFiles="uploadedFiles"
          v-model:isSubmitting="isSubmitting"
          v-model:filesPath="filesPath"
        />
      </div>

      <template #footer>
        <div class="flex gap-2 items-center">
          <Button
            outlined
            label="Cancel"
            icon="pi pi-times"
            @click="hideDialog"
          />
          <Button
            label="Save"
            icon="pi pi-check"
            :loading="loading"
            @click="saveAttachments"
          />
        </div>
      </template>
    </Dialog>
  </div>
</template>

<script setup lang="ts">
import type { Contract } from '@prisma/client'

const contract = defineModel<Contract>('contract', { required: true })

const emit = defineEmits(['refresh'])

const toast = useToast()
const { updateContractById } = useContracts()

// Dialog
const visible = ref(false)

const toggleOpen = async () => {
  visible.value = true
}

defineExpose({
  toggleOpen,
})

const hideDialog = () => {
  visible.value = false
  submitted.value = false
  stagedFiles.value = []
}

// Form
const loading = ref(false)
const submitted = ref(false)

// Files Uploader
const filesPath = ref('contracts')
const stagedFiles = ref([])
const uploaderRef = ref(null)
const isSubmitting = ref(false)
const uploadedFiles = ref<JSON[]>([])

const uploadAttachments = async () => {
  await uploaderRef.value?.uploadFiles()
}

// Save Attachments
const saveAttachments = async () => {
  submitted.value = true

  loading.value = true

  if (!stagedFiles.value.length) {
    toast.add({
      severity: 'error',
      summary: 'Error',
      detail: 'Please select a file',
      life: 3000,
    })
    loading.value = false
    return
  }

  try {
    // Upload Attachments
    const attachments = ref<JSON[]>([])

    await uploadAttachments()

    if (uploadedFiles.value.length > 0) {
      attachments.value = uploadedFiles.value
    }

    const { error } = await updateContractById(contract.value.id, {
      attachments: [...contract.value.attachments, ...attachments.value],
    })

    if (error) {
      toast.add({
        severity: 'error',
        summary: 'Error',
        detail: 'Failed to upload files',
        life: 3000,
      })
      return
    }

    toast.add({
      severity: 'success',
      summary: 'Success',
      detail: 'File/s uploaded successfully',
      life: 3000,
    })

    hideDialog()
    emit('refresh')
  }
  catch (error) {
    console.error('Error uploading file/s:', error)
    toast.add({
      severity: 'error',
      summary: 'Error',
      detail: 'Failed uploading file/s',
      life: 3000,
    })
  }
  finally {
    loading.value = false
  }
}
</script>
